import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// New Feature Providers
import { PhysicianInLoopProvider } from './contexts/PhysicianInLoopContext';
import { PHIProtectionProvider } from './contexts/PHIProtectionContext';
import { TelemedicineProvider } from './contexts/TelemedicineContext';
import { VRProvider } from './contexts/VRContext';

// New PWA Components
import { PWAInstallPrompt, OfflineIndicator } from './components/PWAInstallPrompt';
import { PWAUpdateNotification } from './components/PWAUpdateNotification';
import { FeaturesTestingButton } from './components/FeaturesTestingButton';

// Existing Components
import Header from './components/Header';
import LandingPage from './pages/LandingPage';
import ConditionsPage from './pages/ConditionsPage';
import ConditionDetailPage from './pages/ConditionDetailPage';
import DrugsPage from './pages/DrugsPage';
import DrugDetailPage from './pages/DrugDetailPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import CalculatorsPage from './pages/CalculatorsPage';
import CalculatorDetailPage from './pages/calculators/CalculatorDetailPage';
import SavedReportsPage from './pages/SavedReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// New Demo Page
import FeaturesDemo from './pages/FeaturesDemo';

// Hooks
import { useOffline } from './pwa/useOffline';

function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hideHeader = location.pathname.toLowerCase().startsWith('/auth/');
  const { isOnline, isFullySynced } = useOffline();
  
  return (
    <>
      {/* StripeModeBanner removed - available in /features-demo */}
      <OfflineIndicator isOnline={isOnline} isFullySynced={isFullySynced} />
      {!hideHeader && <Header />}
      {children}
      <PWAInstallPrompt />
      <PWAUpdateNotification />
      <FeaturesTestingButton />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <PHIProtectionProvider>
          <PhysicianInLoopProvider>
            <TelemedicineProvider>
              <VRProvider>
                <Router>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/conditions" element={<ConditionsPage />} />
                      <Route path="/conditions/:slug" element={<ConditionDetailPage />} />
                      <Route path="/drugs" element={<DrugsPage />} />
                      <Route path="/drugs/:slug" element={<DrugDetailPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/calculators" element={<CalculatorsPage />} />
                      <Route path="/calculators/:id" element={<CalculatorDetailPage />} />
                      <Route path="/reports" element={<SavedReportsPage />} />
                      <Route path="/reports/:id" element={<ReportDetailPage />} />
                      <Route path="/feedback" element={<FeedbackPage />} />
                      <Route path="/auth/login" element={<LoginPage />} />
                      <Route path="/auth/register" element={<RegisterPage />} />
                      <Route path="/features-demo" element={<FeaturesDemo />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Layout>
                </Router>
              </VRProvider>
            </TelemedicineProvider>
          </PhysicianInLoopProvider>
        </PHIProtectionProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

function FeedbackPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Submit Feedback</h1>
        <p className="text-gray-600 mb-4">Help us improve OmniClinicalBridge</p>
        <a href="mailto:ceo@omnialera.com" className="text-blue-600 hover:underline">
          Email: ceo@omnialera.com
        </a>
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
        <a href="/" className="text-blue-600 hover:underline">Go to Homepage</a>
      </div>
</div>
  );
}

export default App;
